import { Table, TableProps, Row, Col, Grid, message } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import config from 'config';
import {
  useGetLabworkListQuery,
  LabworkOutput,
  LabworkListSort,
  Order,
  LabworkListFilterInput,
  useGetLocationsQuery,
} from 'gql/types';
import { Spin, TableSearchInput } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import { useSort, useDebounce, useFilter } from 'modules/common/hooks';
import { useRegionalFilterInit } from 'modules/common/hooks/useRegionalFilterInit';
import { getColumns } from 'modules/donor/components/GeneralLabworkTable/columns';
import {
  createGeneralLabworkChipsConfig,
  excludedFilterFields,
} from 'modules/donor/configs/generalLabworkFilters';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { useUserPermissionsAndRegion } from 'modules/user/hooks/useUserPermissionsAndRegion';
import { FC, useMemo } from 'react';
import { getTranslation } from 'utils/getTranslation';

const { useBreakpoint } = Grid;

const GeneralLabworkTable: FC = () => {
  const t = getTranslation('donor.GeneralLabworkTable');

  const { debounce } = useDebounce();
  const pagination = usePagination();
  const labworkSort = useSort<LabworkListSort>();
  const filter = useFilter<LabworkListFilterInput>();

  const { assignedLocations, canViewAllRegions, hasLocationRestrictions } =
    useUserPermissionsAndRegion();

  const assignedLocationNames = useMemo(
    () => assignedLocations.map((l) => l.name),
    [assignedLocations]
  );

  const { isLoading: isRegionalFilterLoading } =
    useRegionalFilterInit<LabworkListFilterInput>({
      filterHook: filter,
      filterFieldName: 'locationName',
      canViewAllRegions,
      hasLocationRestrictions,
      assignedLocationNames,
      successMessage: t('regionalFilterApplied'),
    });

  const { data: allAvailableLocationsData } = useGetLocationsQuery({
    variables: {
      input: {
        skip: 0,
        take: 1000,
      },
    },
  });

  const locationFilterOptions =
    allAvailableLocationsData?.getLocations.data.map((location) => ({
      text: location.name,
      value: location.name,
    }));

  const chipsConfig = useMemo(() => createGeneralLabworkChipsConfig(t), [t]);

  const labworkFilter = filter.getFilterObject();

  const { data, loading: getLabworkListLoading } = useGetLabworkListQuery({
    variables: {
      input: {
        filter: labworkFilter,
        take: config.PAGINATION.PAGE_SIZE,
        skip: pagination.skip,
        sort: labworkSort.options,
      },
    },
    skip: isRegionalFilterLoading,
    onCompleted: (result) => {
      pagination.setTotal(result.getLabworkList.meta.total);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const isLoading = getLabworkListLoading || isRegionalFilterLoading;
  const labworks = data?.getLabworkList.data as LabworkOutput[];

  const onTableChange: TableProps<LabworkOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<LabworkOutput>;
      const fieldName =
        LabworkListSort[
          tableSorter.columnKey?.toString() as keyof typeof LabworkListSort
        ];
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        labworkSort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        labworkSort.removeSortOption(fieldName);
      }
    }

    if (extra.action === 'filter') {
      Object.keys(filters).forEach((fieldName) => {
        filter?.setFilterOption({
          fieldName: fieldName as keyof LabworkListFilterInput,
          value: (filters as any)[fieldName],
        });
      });
    }
  };

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    filter.setFilterOption({
      fieldName: 'searchingQuery',
      value,
    });
  };

  const { md } = useBreakpoint();

  const columns = getColumns({
    showSorterTooltip: md,
    showFilters: labworkFilter !== undefined,
    sort: labworkSort.options[0],
    filter: labworkFilter,
    locationFilterOptions,
  });

  return (
    <Spin spinning={isLoading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <TableSearchInput
            onSearchChange={debounce(handleSearch)}
            placeholder={t('searchLabwork')}
            maxLength={100}
          />
        </Col>

        <Col span={24}>
          <FilterChips<LabworkListFilterInput>
            filterHook={filter}
            config={chipsConfig}
            excludeFields={excludedFilterFields}
          />
        </Col>

        <Col span={24}>
          <Table
            rowKey={({ id }) => id}
            columns={columns}
            dataSource={labworks}
            onChange={onTableChange}
            pagination={pagination.config}
            scroll={{ x: 750 }}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default GeneralLabworkTable;
